'use client';

import React, { ReactElement } from 'react';
import { RiSparkling2Line } from 'react-icons/ri';
import ReactHtmlParser from 'react-html-parser';

export type ColorDefaultChoice = 'pink' | 'purple' | 'blue' | 'cyan' | 'green' | 'emerald';
type Props = {
  subtitle: string;
  description: string;
  title: string;
  sections: Sections[];
};
type Sections = {
  title: string;
  description: string;
  metric: string;
  outlineIcon: ReactElement;
  metricLabel: string;
  colorFrom: ColorDefaultChoice;
  colorTo: ColorDefaultChoice;
};

const WhyChooseThisBot = (props: Props) => {
  const items = props.sections.map((section: Sections, index: number) => {
    const colorVariants: { [key: string]: { [key: string]: string } } = {
      pink: {
        purple: 'tw-from-pink-500 tw-to-purple-600',
      },
      purple: {
        pink: 'tw-from-purple-500 tw-to-pink-600',
      },
      blue: {
        cyan: 'tw-from-blue-500 tw-to-cyan-600',
      },
      cyan: {
        blue: 'tw-from-cyan-500 tw-to-blue-600',
      },
      green: {
        emerald: 'tw-from-green-500 tw-to-emerald-600',
      },
      emerald: {
        green: 'tw-from-emerald-500 tw-to-green-600',
      },
    };
    const lightColorVariants: { [key: string]: { [key: string]: string } } = {
      pink: {
        purple: 'tw-from-pink-50 tw-to-purple-50',
      },
      purple: {
        pink: 'tw-from-purple-50 tw-to-pink-50',
      },
      blue: {
        cyan: 'tw-from-blue-50 tw-to-cyan-50',
      },
      cyan: {
        blue: 'tw-from-cyan-50 tw-to-blue-60',
      },
      green: {
        emerald: 'tw-from-green-50 tw-to-emerald-50',
      },
      emerald: {
        green: 'tw-from-emerald-50 tw-to-green-50',
      },
    };
    const bgColorVariants: { [key: string]: string } = {
      pink: 'tw-bg-pink-50',
      purple: 'tw-bg-purple-50',
      blue: 'tw-bg-blue-50',
      cyan: 'tw-bg-cyan-50',
      green: 'tw-bg-green-50',
      emerald: 'tw-bg-emerald-50',
    };
    const borderColorVariants: { [key: string]: string } = {
      pink: 'tw-border-pink-200',
      purple: 'tw-border-purple-200',
      blue: 'tw-border-blue-200',
      cyan: 'tw-border-cyan-200',
      green: 'tw-border-green-200',
      emerald: 'tw-border-emerald-200',
    };
    return {
      iconicText: section.metric,
      title: section.title,
      description: section.description,
      outlineIcon: section.outlineIcon,
      color: colorVariants[section.colorFrom]?.[section.colorTo] || '',
      lightColor: lightColorVariants[section.colorFrom]?.[section.colorTo] || '',
      borderColor: borderColorVariants[section.colorFrom] || '',
      bgColor: bgColorVariants[section.colorFrom] || '',
      delay: `${index * 150}ms`,
      metricLabel: section.metricLabel,
    };
  });

  return (
    <section className="tw-pt-10 lg:tw-pt-24 tw-relative tw-overflow-hidden tw-font-['Lexend'] tw-bg-transparent">
      {/* Background decorations */}
      <div className="tw-absolute tw-inset-0">
        <div className="tw-absolute tw-top-1/4 tw-right-1/4 tw-w-72 tw-h-72 tw-bg-blue-100/30 tw-rounded-full tw-blur-3xl tw-animate-pulse"></div>
        <div
          className="tw-absolute tw-bottom-1/4 tw-left-1/4 tw-w-80 tw-h-80 tw-bg-purple-100/20 tw-rounded-full tw-blur-3xl tw-animate-pulse"
          style={{ animationDelay: '2s' }}
        ></div>
        <div
          className="tw-absolute tw-top-1/2 tw-left-1/2 tw-transform tw--translate-x-1/2 tw--translate-y-1/2 tw-w-96 tw-h-96 tw-bg-green-100/10 tw-rounded-full tw-blur-3xl tw-animate-pulse"
          style={{ animationDelay: '4s' }}
        ></div>
      </div>

      <div className="tw-container tw-mx-auto tw-px-4 sm:tw-px-6 lg:tw-px-8 tw-max-w-7xl tw-relative tw-z-10">
        {/* Header */}
        <div className="tw-text-center tw-mb-20">
          <div className="tw-inline-flex tw-items-center tw-gap-2 tw-bg-gradient-to-r tw-from-blue-100 tw-to-purple-100 tw-text-blue-700 tw-px-6 tw-py-3 tw-rounded-full tw-text-sm tw-font-semibold tw-mb-8 tw-shadow-sm tw-border tw-border-blue-200/50">
            <RiSparkling2Line className="tw-w-4 tw-h-4" />
            {props.subtitle}
          </div>
          <h2 className="tw-text-4xl sm:tw-text-5xl lg:tw-text-6xl tw-font-bold tw-text-gray-900 tw-mb-6 tw-leading-tight">
            {props.title}
          </h2>
          <p className="tw-text-xl tw-text-gray-600 tw-max-w-4xl tw-mx-auto tw-leading-relaxed">
            {ReactHtmlParser(props.description)}
          </p>
        </div>

        {/* Stats Cards */}
        <div className="tw-grid sm:tw-grid-cols-1 lg:tw-grid-cols-3 tw-gap-8 tw-mb-20">
          {items.map((item, index) => {
            return (
              <div
                key={index}
                className="tw-group tw-relative tw-bg-white tw-rounded-3xl tw-shadow-xl tw-border tw-border-gray-100 tw-p-8 lg:tw-p-10 tw-transition-all tw-duration-700 hover:tw-shadow-2xl hover:tw-scale-[1.02] tw-overflow-hidden tw-cursor-pointer"
                style={{
                  animationDelay: item.delay,
                  animation: 'fadeInUp 0.8s ease-out forwards',
                }}
              >
                {/* Background gradient overlay */}
                <div
                  className={`tw-absolute tw-inset-0 tw-bg-gradient-to-br ${item.lightColor} tw-opacity-0 group-hover:tw-opacity-100 tw-transition-all tw-duration-500`}
                ></div>

                {/* Content */}
                <div className="tw-relative tw-z-10 tw-h-full tw-flex tw-flex-col">
                  {/* Header with icon and main metric */}
                  <div className="tw-flex tw-items-start tw-justify-between tw-mb-8">
                    <div className="tw-text-left">
                      <div
                        className={`tw-text-4xl lg:tw-text-5xl tw-font-bold tw-bg-gradient-to-r ${item.color} tw-bg-clip-text tw-text-transparent tw-mb-1 tw-pb-1.5`}
                      >
                        {item.iconicText}
                      </div>
                    </div>
                  </div>

                  {/* Title */}
                  <h3 className="tw-text-2xl lg:tw-text-3xl tw-font-bold tw-text-gray-900 tw-mb-4 tw-leading-tight">
                    {item.title}
                  </h3>

                  {/* Description - with flex-grow to push the benefit block down */}
                  <p className="tw-text-gray-600 tw-leading-relaxed tw-mb-8 tw-text-sm tw-flex-grow">
                    {ReactHtmlParser(item.description)}
                  </p>

                  {/* Key benefit - this will now be at the same level across all cards */}
                  <div
                    className={`tw-flex tw-items-center tw-gap-3 tw-p-4 tw-rounded-2xl ${item.bgColor} tw-border ${item.borderColor} tw-mb-6 tw-mt-auto`}
                  >
                    {item.outlineIcon}
                    <div>
                      <div className="tw-font-bold tw-text-gray-900 tw-text-lg">{item.iconicText}</div>
                      <div className="tw-text-sm tw-text-gray-600">{item.metricLabel}</div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <style jsx>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(40px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </section>
  );
};

export default WhyChooseThisBot;
